/* eslint-disable no-alert */
/* eslint-disable no-restricted-globals */
import { ThemeProvider } from "@mui/material";
import { StyledEngineProvider } from "@mui/material/styles";
import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import TimeAgo from "javascript-time-ago";
import en from "javascript-time-ago/locale/en.json";
import { SnackbarProvider } from "notistack";
import React from "react";
import ReactDOM from "react-dom";
import { Provider } from "react-redux";

import App from "App";
import { ErrorView } from "components/common/ErrorView/ErrorView";
import { ErrorType } from "components/common/ErrorView/types";
import { INTEG, PRODUCTION, REVIEW } from "config/constants";
import { ProviderFetch } from "config/UseFetchProvider";
import { ServiceWorkerProvider } from "context/serviceWorkerContext";
import ErrorSnackbar from "new-components/errorSnackbar/ErrorSnackbar";
import { setupStore } from "store";
import theme from "theme/theme";

import { getConfig } from "./config/config";
import "./index.css";
import { logError } from "./utils/errorBoundaryLogger";

const config = getConfig();

TimeAgo.addDefaultLocale(en);

const REACT_QUERY_DEVTOOLS_AVAILABLE_ENVIRONMENTS = ["eksdev"];

function Fallback() {
  return <ErrorView error={ErrorType.ERROR_RENDERING} />;
}

const SentryAvailableEnvironments = [PRODUCTION, INTEG, REVIEW];
if (config.REACT_APP_SENTRY_DSN && SentryAvailableEnvironments.includes(config.REACT_APP_STAGE)) {
  Sentry.init({
    dsn: config.REACT_APP_SENTRY_DSN,
    environment: config.REACT_APP_STAGE,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        maskAllInputs: false,
        blockAllMedia: false,
      }),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0.25,
  });
}

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: 0,
    },
  },
});

ReactDOM.render(
  <ThemeProvider theme={theme}>
    <StyledEngineProvider injectFirst>
      <Provider store={setupStore()}>
        <SnackbarProvider
          preventDuplicate
          anchorOrigin={{ horizontal: "center", vertical: "top" }}
          autoHideDuration={3000}
          Components={{
            customError: ErrorSnackbar,
          }}
        >
          <QueryClientProvider client={queryClient}>
            <ProviderFetch>
              <Sentry.ErrorBoundary
                fallback={Fallback}
                onError={(error, errorInfo) => logError(error, errorInfo, config)}
              >
                <ServiceWorkerProvider>
                  <App />
                </ServiceWorkerProvider>
              </Sentry.ErrorBoundary>
            </ProviderFetch>
            {REACT_QUERY_DEVTOOLS_AVAILABLE_ENVIRONMENTS.includes(config.REACT_APP_STAGE) && (
              <ReactQueryDevtools initialIsOpen={false} />
            )}
          </QueryClientProvider>
        </SnackbarProvider>
      </Provider>
    </StyledEngineProvider>
  </ThemeProvider>,
  document.getElementById("root")
);
